import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Skeleton, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetCampaignLineItemsQuery } from '@/api';
import { NAMESPACE } from '@/constants';
import type { BookingRow } from '@/models';

interface Props {
  advertiser: string;
  campaign: string;
  columns: ColumnsType<BookingRow>;
  /** How many lines the grouped row said this campaign has, so the fetch can
   *  ask for them all in one page rather than paginating inside a row. */
  lineItemCount: number;
}

/**
 * The line items behind one campaign row, fetched when the row is expanded.
 *
 * The list endpoint returns campaigns already collapsed -- that is what makes
 * its page numbers describe the rows on screen -- so the individual lines are
 * no longer in the page payload and have to be asked for.
 */
export const CampaignLineItems: FC<Props> = ({
  advertiser,
  campaign,
  columns,
  lineItemCount
}) => {
  const { t } = useTranslation(NAMESPACE.BOOKING_SHEET_PAGE);

  const { data, isLoading, isError } = useGetCampaignLineItemsQuery({
    page: 1,
    // One page holding the whole campaign: a nested table that paginates is
    // worse than one that is briefly tall.
    pageSize: Math.max(lineItemCount, 1),
    advertiser,
    campaign
  });

  if (isError) {
    return <Alert type="error" role="alert" showIcon message={t('lineItemsLoadFailed')} />;
  }

  if (isLoading) return <Skeleton active paragraph={{ rows: Math.min(lineItemCount, 4) }} />;

  return (
    <Table<BookingRow>
      rowKey="nrve_line_item_id"
      columns={columns}
      dataSource={data?.rows ?? []}
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
    />
  );
};
